import useSWR from 'swr';
import { localeAtom } from '@repo/i18n';
import {
    MembershipBooking,
    MembershipIntentMultiResponse,
    MembershipOrder,
    MembershipUser,
    MembershipUserValueCard,
    MembershipValueCardProduct,
} from '@repo/types';
import { getUrlWithParams, apiUrl, MembershipIntentMultiRequest } from '../../fetchers/membership';
import { get, post } from '../../utils/api-client-common';
import { ApiError } from '../../utils/ApiError';

export function useLatestValueCardSubscriptionOrder(valueCardId: number) {
    const { locale } = localeAtom.subject.value;

    const url = getUrlWithParams(
        apiUrl() + `/Users/me/valueCards/${valueCardId}/subscription/latest-order`,
        locale,
        {},
    );
    const { data, error, isLoading, mutate } = useSWR<MembershipOrder>(url, get);

    return {
        data: data ?? undefined,
        error,
        isLoading,
        mutate,
    };
}

export function useLoggedInUser(apiToken: string | null) {
    const { locale } = localeAtom.subject.value;

    const url = getUrlWithParams(apiUrl() + '/Users/me', locale);
    const key = apiToken ? url : null;
    const { data, error, isLoading, mutate } = useSWR<MembershipUser>(
        key,
        async () => {
            const response = await get<MembershipUser>(url);
            return response;
        },
        {
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        error,
        isLoading,
        mutate,
    };
}

export function useAvailableValueCardProductsForUser(apiToken: string | null, siteKey?: string) {
    const { locale } = localeAtom.subject.value;

    const url = getUrlWithParams(
        apiUrl() + `/Users/me/availableValueCardProducts/site/${siteKey}`,
        locale,
    );
    const key = apiToken && siteKey ? url : null;

    const { data, error, isLoading, mutate } = useSWR<MembershipValueCardProduct[]>(
        key,
        async () => {
            try {
                const response = await get<MembershipValueCardProduct[]>(url);
                return response;
            } catch (error) {
                if (error instanceof ApiError)
                    error.message = localeAtom.subject.value.t.couldntGetValueCardProducts;

                throw error;
            }
        },
        {
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        error,
        isLoading,
        mutate,
    };
}

export function useValueCardsForUser(apiToken: string | null) {
    const { locale } = localeAtom.subject.value;

    const url = getUrlWithParams(apiUrl() + `/Users/me/valueCards`, locale);
    const key = apiToken ? url : null;

    const { data, error, isLoading, mutate } = useSWR<MembershipUserValueCard[]>(
        key,
        async () => {
            try {
                const response = await get<MembershipUserValueCard[]>(url);
                return response;
            } catch (error) {
                if (error instanceof ApiError)
                    error.message = localeAtom.subject.value.t.couldntGetValueCards;

                throw error;
            }
        },
        {
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        error,
        isLoading,
        mutate,
    };
}

export function useBookingsForUser(user: MembershipUser | null) {
    const { locale } = localeAtom.subject.value;

    const url = getUrlWithParams(apiUrl() + `/Users/me/bookings`, locale);
    const key = user ? url : null;

    const { data, error, isLoading, mutate } = useSWR<MembershipBooking[]>(
        key,
        async () => {
            const response = await get<MembershipBooking[]>(url);
            return response;
        },
        {
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        error,
        isLoading,
        mutate,
    };
}

export function useIntentMulti(
    request: MembershipIntentMultiRequest | null,
    user: MembershipUser | null,
    siteKey?: string,
) {
    const { locale } = localeAtom.subject.value;

    const url = getUrlWithParams(apiUrl() + `/Bookings/site/${siteKey}/intent-multi`, locale, {});

    const keyIfFetch = {
        userId: user?.id,
        activityReservations: request?.activityReservations,
        timeslotReservations: request?.timeslotReservations,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        coupon_code: request?.coupon_code,
    };

    const key = siteKey && request ? keyIfFetch : null;

    const { data, error, isLoading, mutate } = useSWR<MembershipIntentMultiResponse>(
        key,
        async () => {
            const response = await post(url, request);
            return response;
        },
        {
            revalidateOnFocus: false,
            dedupingInterval: 1000 * 10, // We'll assume the prices are valid for at least 10 seconds.
        },
    );

    return {
        data,
        error,
        isLoading,
        mutate,
    };
}
